import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../teacher/AuthContext';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
    const [resetMessage, setResetMessage] = useState('');
    
    const { login, currentUser, loading } = useAuth();
    const navigate = useNavigate();
    const API_URL = window.location.origin;

    // Route auto-guard: If already logged in, redirect them out immediately
    useEffect(() => {
        if (!loading && currentUser) {
            if (currentUser.role === 'admin') {
                navigate('/tutoring/admin-dashboard');
            } else {
                navigate('/roleselection');
            }
        }
    }, [currentUser, loading, navigate]);

    if (loading) {
        return <div className="loading-spacer min-h-screen bg-gray-50"></div>; // Prevents "flicker"
    }

    // Handles traditional email password login
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            const response = await fetch(`${API_URL}/api/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (response.ok) {
                // 1. Save token and update state via AuthContext
                login(data.token, data.user);

                // 2. Logic-based Redirection
                if (data.user.role === 'admin') {
                    navigate('/tutoring/admin-dashboard');
                } else {
                    navigate('/roleselection');
                }
            } else {
                setError(data.message || 'Login failed');
            }
        } catch (err) {
            console.error("Login Error:", err);
            setError(`Technical Error: ${err.message}`);
        }
    };

    // Handles the password recovery email trigger
    const handleForgotPasswordSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setResetMessage('');

        try {
            const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await response.json();

            if (response.ok) {
                setResetMessage('A reset link has been dispatched to your email address.');
                setTimeout(() => setForgotPasswordMode(false), 5000);
            } else {
                setError(data.message || 'Failed to initialize reset process.');
            }
        } catch (err) {
            setError(`Server communication fault: ${err.message}`);
        }
    };

    // Handles OAuth Redirections (Google, Apple, Facebook, X, Instagram)
    const handleSocialLogin = (provider) => {
        window.location.href = `${API_URL}/api/auth/${provider}`;
    };

    return (
        <div className="pt-24 flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
            <div className="w-full max-w-md p-8 bg-white border border-gray-200 rounded-xl shadow-sm">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                    {forgotPasswordMode ? 'Reset Password' : 'Login to TRiANf'}
                </h2>
                
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg">
                        {error}
                    </div>
                )}
                
                {resetMessage && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg font-medium">
                        {resetMessage}
                    </div>
                )}

                {/* ================= CONDITION 1: FORGOT PASSWORD INTERFACE ================= */}
                {forgotPasswordMode ? (
                    <form onSubmit={handleForgotPasswordSubmit} className="space-y-5">
                        <p className="text-sm text-gray-500 leading-relaxed">
                            Enter the email address tied to your account and we'll send over instructions to update your password credentials safely.
                        </p>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email:</label>
                            <input 
                                type="email" 
                                className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                value={email} 
                                onChange={(e) => setEmail(e.target.value)} 
                                required 
                                placeholder="email@example.com"
                            />
                        </div>
                        <button 
                            type="submit" 
                            className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md transition-colors duration-200"
                        >
                            Send Recovery Email
                        </button>
                        <button 
                            type="button" 
                            onClick={() => { setForgotPasswordMode(false); setError(''); }}
                            className="w-full text-center text-sm font-medium text-gray-500 hover:text-gray-700 transition"
                        >
                            Back to standard sign-in
                        </button>
                    </form>
                ) : (
                    /* ================= CONDITION 2: TRADITIONAL LOGIN FORM ================= */
                    <>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email:</label>
                                <input 
                                    type="email" 
                                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                    value={email} 
                                    onChange={(e) => setEmail(e.target.value)} 
                                    required 
                                    placeholder="email@example.com"
                                />
                            </div>
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="text-sm font-medium text-gray-700">Password:</label>
                                    <button 
                                        type="button"
                                        onClick={() => { setForgotPasswordMode(true); setError(''); }}
                                        className="text-xs font-semibold text-blue-600 hover:underline hover:text-blue-800"
                                    >
                                        Forgot Password?
                                    </button>
                                </div>
                                <input 
                                    type="password" 
                                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                    value={password} 
                                    onChange={(e) => setPassword(e.target.value)} 
                                    required 
                                    placeholder="••••••••"
                                />
                            </div>
                            <button 
                                type="submit" 
                                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md transition-colors duration-200"
                            >
                                Login
                            </button>
                        </form>

                        {/* Divider Layout */}
                        <div className="relative my-6 text-center">
                            <hr className="border-gray-200" />
                            <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs text-gray-400 font-medium uppercase tracking-wider">
                                Or continue with
                            </span>
                        </div>

                        {/* ================= SOCIAL AUTHENTICATION BLOCK ================= */}
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                {/* Google */}
                                <button 
                                    onClick={() => handleSocialLogin('google')}
                                    className="flex items-center justify-center gap-2 py-2 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                                >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                                        <path fill="#EA4335" d="M12.24 10.285V14.4h6.887c-.275 1.565-1.88 4.604-6.887 4.604-4.33 0-7.866-3.577-7.866-8s3.536-8 7.866-8c2.46 0 4.105 1.025 5.047 1.926l3.227-3.107C18.316 1.704 15.518 0 12.24 0 5.58 0 0 5.373 0 12s5.58 12 12.24 12c6.96 0 11.57-4.834 11.57-11.743 0-.79-.085-1.398-.188-1.972H12.24z"/>
                                    </svg>
                                    Google
                                </button>
                                {/* Apple */}
                                <button 
                                    onClick={() => handleSocialLogin('apple')}
                                    className="flex items-center justify-center gap-2 py-2 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                                >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.21.67-2.93 1.49-.62.69-1.16 1.84-1.01 2.96 1.12.09 2.27-.57 2.95-1.39z"/>
                                    </svg>
                                    Apple
                                </button>
                            </div>

                            {/* Secondary Social Options Block */}
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                {/* Facebook */}
                                <button 
                                    onClick={() => handleSocialLogin('facebook')}
                                    className="flex items-center justify-center gap-1.5 py-2 px-2 border border-gray-200 rounded-lg font-medium text-gray-600 hover:bg-gray-50 transition"
                                >
                                    <svg className="w-3.5 h-3.5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c4.56-.93 8-4.96 8-9.8z"/>
                                    </svg>
                                    Facebook
                                </button>
                                {/* X / Twitter */}
                                <button 
                                    onClick={() => handleSocialLogin('twitter')}
                                    className="flex items-center justify-center gap-1.5 py-2 px-2 border border-gray-200 rounded-lg font-medium text-gray-600 hover:bg-gray-50 transition"
                                >
                                    <svg className="w-3.5 h-3.5 text-black" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                    </svg>
                                    X Account
                                </button>
                                {/* Instagram */}
                                <button 
                                    onClick={() => handleSocialLogin('instagram')}
                                    className="flex items-center justify-center gap-1.5 py-2 px-2 border border-gray-200 rounded-lg font-medium text-gray-600 hover:bg-gray-50 transition"
                                >
                                    <svg className="w-3.5 h-3.5 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.051.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                                    </svg>
                                    Instagram
                                </button>
                            </div>
                        </div>
                    </>
                )}
                
                {/* Footer Switch */}
                <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                    <p className="text-sm text-gray-600">Don't have an account?</p>
                    <button 
                        onClick={() => navigate('/register')} 
                        className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                    >
                        Register for an Account
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Login;